import type { GameSettings } from "@rosette/ts/src/royalur/model/GameSettings.js";
import { PathType } from "@rosette/ts/src/royalur/model/path/PathType.js";
import { plural } from "@rosette/ts/src/util/formatting.js";
import { RoyalUrDice } from "@rosette/ts/src/business/game/royalur/RoyalUrDice.js";
import { DropdownAndLabel } from "@rosette/web_components/src/form/input/DropdownAndLabel.js";
import { CheckboxAndLabel } from "@rosette/web_components/src/form/input/CheckboxAndLabel.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";


interface GameSettingsSelectorProps {
    customGameSettings: GameSettings;
    setCustomGameSettings: (gameSettings: GameSettings) => void;
}

/**
 * A selector for choosing the game settings.
 */
export function GameSettingsSelector({
    customGameSettings, setCustomGameSettings,
}: GameSettingsSelectorProps) {
    return (
        <Panel className="flex w-full flex-col items-start text-left mb-6">
            <DropdownAndLabel
                id="dice_type"
                className="w-full max-w-md"
                value={customGameSettings.getDice().getID()}
                setValue={diceID => setCustomGameSettings(
                    customGameSettings.withDice(RoyalUrDice.getByID(diceID)),
                )}
                options={RoyalUrDice.values().map(diceType => ({
                    value: diceType.getID(),
                    label: diceType.getDesc(),
                }))}>

                Dice
            </DropdownAndLabel>
            <DropdownAndLabel
                id="path_type"
                className="w-full max-w-md"
                value={customGameSettings.getPaths().getID()}
                setValue={pathID => setCustomGameSettings(
                    customGameSettings.withPaths(PathType.getByID(pathID)),
                )}
                options={PathType.forStandardBoard().map(pathType => ({
                    value: pathType.getID(),
                    label: pathType.getDesc(),
                }))}>

                Path
            </DropdownAndLabel>
            <DropdownAndLabel
                id="piece_count"
                className="w-full max-w-md"
                value={`${customGameSettings.getStartingPieceCount()}`}
                setValue={pieceCount => setCustomGameSettings(
                    customGameSettings.withStartingPieceCount(parseInt(pieceCount)),
                )}
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(pieceCount => ({
                    value: `${pieceCount}`,
                    label: (
                        `${pieceCount} ${plural("piece", pieceCount)}`
                        + (pieceCount === 7 ? " (default)" : "")
                    ),
                }))}>

                Pieces
            </DropdownAndLabel>
            <CheckboxAndLabel
                id="safe_rosettes"
                name="safe_rosettes"
                checked={customGameSettings.areRosettesSafe()}
                setChecked={checked => setCustomGameSettings(
                    customGameSettings.withSafeRosettes(checked),
                )}>

                Pieces on rosettes are safe from capture
            </CheckboxAndLabel>
            <CheckboxAndLabel
                id="rosettes_reroll"
                name="rosettes_reroll"
                checked={customGameSettings.doRosettesGrantExtraRolls()}
                setChecked={checked => setCustomGameSettings(
                    customGameSettings.withRosettesGrantExtraRolls(checked),
                )}>

                Rosettes grant an extra roll
            </CheckboxAndLabel>
            <CheckboxAndLabel
                id="captures_reroll"
                name="captures_reroll"
                checked={customGameSettings.doCapturesGrantExtraRolls()}
                setChecked={checked => setCustomGameSettings(
                    customGameSettings.withCapturesGrantExtraRolls(checked),
                )}>

                Captures grant an extra roll
            </CheckboxAndLabel>
        </Panel>
    );
}
